import { RefreshCw, Users } from "lucide-react";

import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@workspace/ui/components/avatar";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import type { Shift, StaffMember } from "@workspace/venue-management";
import { RoleDisplay } from "@workspace/venue-management";

import {
	checkedInStaff,
	STAFF_PICKER_COPY,
	staffPickerState,
} from "./staff-picker-state";

interface StaffSelectionProps {
	staffMembers: StaffMember[];
	shifts: Shift[];
	onSelectStaff: (staff: StaffMember) => void;
	/** Refetches the kiosk data behind the picker. HA-663. */
	onRefresh?: () => void;
	isRefreshing?: boolean;
}

export function StaffSelection({
	staffMembers,
	shifts,
	onSelectStaff,
	onRefresh,
	isRefreshing = false,
}: StaffSelectionProps) {
	// Through the shared helper, so the buttons drawn below and the message shown instead of them
	// cannot disagree about who is on shift.
	const onShiftStaff = checkedInStaff(staffMembers, shifts);
	const state = staffPickerState(staffMembers, shifts);

	const offShiftStaff: StaffMember[] = [];

	if (state !== "ready") {
		const copy = STAFF_PICKER_COPY[state];

		return (
			<div
				className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed p-8 text-center"
				role="status"
			>
				<Users className="size-8 text-muted-foreground" />
				<div className="space-y-1">
					<p className="font-medium">{copy.title}</p>
					<p className="text-sm text-muted-foreground">{copy.detail}</p>
				</div>
				{onRefresh && (
					<Button
						variant="outline"
						onClick={onRefresh}
						disabled={isRefreshing}
						aria-label="Refresh staff list"
					>
						<RefreshCw
							className={cn("mr-2 size-4", isRefreshing && "animate-spin")}
						/>
						Refresh
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
					{onShiftStaff.map((staff) => (
						<Button
							key={staff.id}
							variant="outline"
							className="flex h-32 flex-col items-center justify-center p-2 text-center"
							onClick={() => onSelectStaff(staff)}
							aria-label={`Select ${staff.personalDetails.preferredName}`}
						>
							<span className="text-sm font-medium">
								{staff.personalDetails.preferredName}
							</span>
							<Badge variant="outline" className="border-primary">
								<RoleDisplay value={staff.role.label} />
							</Badge>
						</Button>
					))}
				</div>
			</div>

			{/* Off Shift Staff */}
			{offShiftStaff.length > 0 && (
				<div className="space-y-4">
					<h3 className="flex items-center text-lg font-medium">
						<Users className="mr-2 size-5 text-gray-400" />
						Off Shift
					</h3>
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
						{offShiftStaff.map((staff) => (
							<Button
								key={staff.id}
								variant="outline"
								className="flex h-32 flex-col items-center justify-center p-2 text-center opacity-80 hover:opacity-100"
								onClick={() => onSelectStaff(staff)}
								aria-label={`Select ${staff.personalDetails.preferredName}`}
							>
								<Avatar className="mb-2 size-16">
									<AvatarImage
										src={staff.personalDetails.image}
										alt={staff.personalDetails.preferredName}
										className="grayscale"
									/>
									<AvatarFallback>
										{staff.personalDetails.preferredName
											.split(" ")
											.map((n) => n[0])
											.join("")}
									</AvatarFallback>
								</Avatar>
								<span className="text-sm font-medium">
									{staff.personalDetails.preferredName}
								</span>
								<Badge
									variant="outline"
									className="mt-1 border-gray-500 text-gray-600"
								>
									<RoleDisplay value={staff.role.value} />
								</Badge>
							</Button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
